/**
 * DOMSanitizer.ts
 * Utilitaire de sanitisation HTML pour prévenir les attaques XSS
 * Wrapper autour de DOMPurify
 */
import DOMPurify from 'dompurify';

/**
 * Nettoie le HTML pour éviter les attaques XSS
 * @param html - Contenu HTML à nettoyer
 * @returns HTML nettoyé et sécurisé
 */
export function sanitizeHTML(html: string): string {
  if (typeof html !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'div',
      'span',
      'p',
      'br',
      'strong',
      'em',
      'u',
      'i',
      'b',
      'ul',
      'ol',
      'li',
      'a',
      'img',
      'button',
      'input',
      'select',
      'option',
      'label',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      // CORRIGÉ: Autoriser les balises SVG pour les icônes Lucide
      'svg',
      'path',
      'g',
      'circle',
      'rect',
      'line',
      'polyline',
      'polygon',
      'ellipse',
      'text',
      'defs',
      'use',
    ],
    ALLOWED_ATTR: [
      'class',
      'id',
      'data-*',
      'aria-*',
      'role',
      'tabindex',
      'src',
      'alt',
      'title',
      'href',
      'target',
      'rel',
      'type',
      'value',
      'name',
      'for',
      'data-lucide',
      // CORRIGÉ: Autoriser les attributs SVG pour les icônes Lucide
      'viewBox',
      'fill',
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'stroke-miterlimit',
      'd',
      'x',
      'y',
      'width',
      'height',
      'cx',
      'cy',
      'r',
      'rx',
      'ry',
      'points',
      'transform',
      'opacity',
      'xmlns',
    ],
    ALLOW_DATA_ATTR: true,
  });
}

/**
 * Définit le contenu innerHTML de manière sécurisée
 * @param element - Élément DOM à modifier
 * @param html - Contenu HTML à définir (sera automatiquement nettoyé)
 */
export function safeSetInnerHTML(element: HTMLElement, html: string): void {
  if (!element || typeof html !== 'string') {
    return;
  }
  element.innerHTML = sanitizeHTML(html);
}

/**
 * Crée un élément DOM avec du contenu HTML sécurisé
 * @param tag - Nom de la balise HTML
 * @param html - Contenu HTML (sera automatiquement nettoyé)
 * @param attributes - Attributs à ajouter à l'élément
 * @returns Élément DOM créé
 */
export function createElementWithSafeHTML(
  tag: string,
  html: string,
  attributes?: Record<string, string>
): HTMLElement {
  const element = document.createElement(tag);
  if (html) {
    safeSetInnerHTML(element, html);
  }
  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      // Vérifier que l'attribut est autorisé
      if (
        key.startsWith('data-') ||
        key.startsWith('aria-') ||
        [
          'class',
          'id',
          'type',
          'name',
          'for',
          'src',
          'alt',
          'title',
          'href',
          'target',
          'rel',
        ].includes(key)
      ) {
        element.setAttribute(key, value);
      }
    });
  }
  return element;
}

/**
 * Vérifie si une chaîne contient du HTML potentiellement dangereux
 * @param html - Contenu HTML à vérifier
 * @returns true si le contenu est suspect
 */
export function containsDangerousHTML(html: string): boolean {
  if (typeof html !== 'string') {
    return false;
  }
  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<object\b/gi,
    /<embed\b/gi,
  ];
  return dangerousPatterns.some(pattern => pattern.test(html));
}
